import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "./schema.gen";

const CSRF_COOKIE = "tbis_csrf";
const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** A problem+json error from the API (ADR-0014). ``code`` is translated with ``errors.<code>``. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

const csrf: Middleware = {
  async onRequest({ request }) {
    if (!UNSAFE.has(request.method)) return request;
    let token = readCookie(CSRF_COOKIE);
    if (!token) {
      // Any GET issues the cookie; the app normally has one already from /system/info.
      await fetch("/api/v1/system/info", { credentials: "same-origin" });
      token = readCookie(CSRF_COOKIE);
    }
    if (token) request.headers.set("X-CSRF-Token", token);
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use(csrf);

export function toApiError(status: number, body: unknown): ApiError {
  if (body && typeof body === "object" && "code" in body && typeof body.code === "string") {
    const fields = "fields" in body && body.fields && typeof body.fields === "object" ? body.fields : {};
    return new ApiError(status, body.code, fields as Record<string, string>);
  }
  return new ApiError(status, status === 0 ? "network" : "unknown");
}

/** Await an openapi-fetch call and return its data, or throw an ``ApiError``. */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch {
    throw new ApiError(0, "network");
  }
  if (!result.response.ok) throw toApiError(result.response.status, result.error);
  return result.data as T;
}
